import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, CheckboxGroup, Combobox, DataTable, DefinitionList, Drawer, EmptyState, ErrorSummary, FormSection, Grid, Inline,
  Icon, PageHeader, Panel, RadioGroup, SaveBar, Select, SideNav, Stack, Switch, t, Tag, Text, toast,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { RefusalPage } from '../../components/Refusal';
import { PullRequestNotice, changeRef } from '../../components/catalogue/PullRequestNotice';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { clockTime, relativeTime, shortDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { PendingButton } from '../../components/PendingButton';
import { FULL_HEIGHT } from '../../lib/tables';

type Rule = maintenance.components['schemas']['RoutingRule'];
type Week = maintenance.components['schemas']['OnCallWeek'];
const SECTIONS = [
  { id: 'channels', icon: 'cell_tower' },
  { id: 'rules', icon: 'alt_route' },
  { id: 'simulation', icon: 'science' },
  { id: 'oncall', icon: 'support_agent' },
  { id: 'teams', icon: 'touch_app' },
  { id: 'options', icon: 'tune' },
] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const WAY_TONES = { operational: 'pos', degraded: 'warn', failing: 'neg', not_configured: 'neutral' } as const;
const CHECKMK_TONES = { applied: 'pos', failed: 'neg', not_configured: 'neutral' } as const;

/**
 * « Canaux et routage » (SPEC-009), DSM Settings page type: who receives what,
 * by which way, tested and simulated; and the on-call schedule. Rules and
 * weeks live in the catalogue repository: each change is a pull request.
 */
export function ChannelsPage() {
  const [params, setParams] = useSearchParams();
  const section: SectionId = SECTIONS.some(s => s.id === params.get('section')) ? (params.get('section') as SectionId) : 'channels';
  const routing = useQuery({ queryKey: ['routing'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/routing')), retry: false });
  const crumbs = [{ label: t('nav.settings') }, { label: t('channels.title') }];
  // A section with changes puts its save bar in the AppShell footer (LayoutSettings).
  const [footer, setFooter] = useState<ReactNode>(null);
  if (routing.error) return <Shell crumbs={crumbs}><RefusalPage error={routing.error} /></Shell>;

  return (
    <Shell crumbs={crumbs} footer={footer}>
      <Stack gap={24}>
        <PageHeader title={t('channels.title')} subtitle={t('channels.subtitle')} />
        <div className="kui-settings">
          <SideNav
            label={t('channels.nav.label')}
            value={section}
            onChange={id => setParams({ section: id })}
            groups={[{ items: SECTIONS.map(s => ({ id: s.id, label: t(`channels.nav.${s.id}`), icon: s.icon })) }]}
          />
          <div className="kui-settings__main">
            {section === 'channels' && <WaysSection />}
            {section === 'rules' && <RulesSection />}
            {section === 'simulation' && <SimulationSection />}
            {section === 'oncall' && <OnCallSection />}
            {section === 'teams' && <TeamsPolicySection onFooter={setFooter} />}
            {section === 'options' && <OptionsSection onFooter={setFooter} />}
          </div>
        </div>
      </Stack>
    </Shell>
  );
}

function WaysSection() {
  const { can } = useSession();
  const channels = useQuery({ queryKey: ['channels'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/channels')), refetchInterval: 30_000 });
  const checkmk = useQuery({ queryKey: ['channels-checkmk'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/checkmk')), refetchInterval: 30_000 });
  const [testing, setTesting] = useState<string | null>(null);
  const test = async (channel: string) => {
    setTesting(channel);
    try {
      const r = unwrap(await api.maintenance.POST('/notifications/channels/{channel}/test', { params: { path: { channel } } }));
      toast({ tone: 'pos', title: r.message });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint, duration: 10_000 });
    } finally {
      setTesting(null);
      void channels.refetch();
    }
  };
  return (
    <Stack gap={16}>
      <Text tone="mute">{t('channels.ways.lead')}</Text>
      <Grid columns="2" gap={16}>
        {(channels.data?.ways ?? []).map(w => (
          <Panel key={w.channel} title={t(`channels.way.${w.channel}`)} actions={<Badge tone={WAY_TONES[w.state]}>{t(`channels.state.${w.state}`)}</Badge>}>
            <Stack gap={12}>
              <DefinitionList
                items={[
                  { label: t('channels.lastSent'), value: w.lastSentAt ? `${clockTime(w.lastSentAt)} · ${relativeTime(w.lastSentAt)}` : t('channels.never') },
                  { label: t('channels.lastFailure'), value: w.lastFailureAt ? `${clockTime(w.lastFailureAt)} · ${w.lastFailureCause ?? ''}` : t('channels.none') },
                  ...(w.channel === 'sms' && w.credit !== undefined && w.credit !== null ? [{ label: t('channels.credit'), value: t('channels.creditValue', { n: w.credit }) }] : []),
                ]}
              />
              {w.state === 'failing' && w.channel === 'teams_bot' && <Alert tone="neg">{t('channels.botFailing')}</Alert>}
              {w.channel === 'teams_workflows' && <Text variant="body-sm" tone="mute">{t('channels.workflowsNote')}</Text>}
              {can('notifications.test') && w.channel !== 'teams_workflows' && w.configured && (
                <div><Button variant="secondary" size="sm" icon="send" disabled={testing !== null} onClick={() => void test(w.channel)}>{testing === w.channel ? t('channels.testing') : t('channels.test')}</Button></div>
              )}
            </Stack>
          </Panel>
        ))}
      </Grid>
      {checkmk.data && (
        // RG-009-1: what Checkmk sends by itself, without the portal (ADR-008).
        <Panel title={t('channels.checkmk.title')} subtitle={t('channels.checkmk.subtitle')} actions={<Badge tone={CHECKMK_TONES[checkmk.data.state]}>{t(`channels.checkmk.state.${checkmk.data.state}`)}</Badge>}>
          <Stack gap={12}>
            <DefinitionList
              items={[
                { label: t('channels.checkmk.at'), value: checkmk.data.at ? `${clockTime(checkmk.data.at)} · ${relativeTime(checkmk.data.at)}` : t('channels.never') },
                { label: t('channels.checkmk.rules'), value: String(checkmk.data.rules) },
                { label: t('channels.checkmk.contacts'), value: String(checkmk.data.contacts) },
              ]}
            />
            {checkmk.data.state === 'failed' && checkmk.data.error && <Alert tone="neg" title={t('channels.checkmk.failed')}>{checkmk.data.error}</Alert>}
            {checkmk.data.skipped.map(x => <Text key={x.rule} variant="body-sm" tone="mute">{t('channels.checkmk.skipped', { rule: x.rule, reason: x.reason })}</Text>)}
          </Stack>
        </Panel>
      )}
    </Stack>
  );
}

const EMPTY_RULE = { event: 'availability_incident', client: null as string | null, project: null as string | null, environment: 'prod', minSeverity: 'critical', destinations: [] as string[], teamsChannel: null as string | null };

function RulesSection() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const routing = useQuery({ queryKey: ['routing'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/routing')) });
  const suspended = useQuery({ queryKey: ['routing-suspended'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/suspended')).items, refetchInterval: 60_000 });
  const suspensionOf = (r: Rule) => suspended.data?.find(x => x.rule === r.id);
  const [clientQuery, setClientQuery] = useState('');
  // The scope of a rule: clients searched on the server (150 and more).
  const clients = useQuery({
    queryKey: ['rule-clients', clientQuery],
    queryFn: async () => unwrap(await api.maintenance.GET('/clients', { params: { query: { q: clientQuery || undefined, pageSize: 50, scope: 'all' } } })),
    enabled: can('notifications.write'),
    retry: false,
  });
  const [draft, setDraft] = useState<typeof EMPTY_RULE | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);
  const data = routing.data;
  const writable = can('notifications.write');
  const channelLabel = (id: string | null) => data?.channels.find(c => c.id === id)?.label ?? id ?? '';
  const destinationTags = (r: Rule) => [
    ...(r.destinations.teamsChannel ? [`Teams · ${channelLabel(r.destinations.teamsChannel)}`] : []),
    ...(r.destinations.onCallPrivate ? [t('channels.dest.onCallPrivate')] : []),
    ...(r.destinations.teamEmail ? [t('channels.dest.teamEmail')] : []),
    ...(r.destinations.onCallSms ? [t('channels.dest.onCallSms')] : []),
  ];
  const fieldError = (field: string) => (failure?.body.details?.fields as { field: string; message: string }[] | undefined)?.find(f => f.field === field)?.message;

  const submit = async () => {
    if (!draft) return;
    setSending(true);
    setFailure(null);
    try {
      unwrap(await api.maintenance.POST('/notifications/rules', {
        body: {
          event: draft.event, client: draft.client, project: draft.project, environment: draft.environment, minSeverity: draft.minSeverity,
          destinations: { teamsChannel: draft.destinations.includes('teams') ? draft.teamsChannel : null, onCallPrivate: draft.destinations.includes('onCallPrivate'), teamEmail: draft.destinations.includes('teamEmail'), onCallSms: draft.destinations.includes('onCallSms') },
        },
      }));
      toast({ tone: 'pos', title: t('channels.rules.proposed') });
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['routing'] });
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };
  const toggle = async (r: Rule) => {
    try {
      unwrap(await api.maintenance.PATCH('/notifications/rules/{rule}', { params: { path: { rule: r.id } }, body: { active: !r.active } }));
      toast({ tone: 'pos', title: t('channels.rules.proposed') });
      void queryClient.invalidateQueries({ queryKey: ['routing'] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    }
  };

  return (
    <Stack gap={16}>
      {!writable && <Alert tone="info">{t('channels.readOnly')}</Alert>}
      {data?.pullRequest && <PullRequestNotice pullRequest={data.pullRequest} />}
      {(data?.invalid ?? []).map(i => <Alert key={i} tone="warn" title={t('channels.rules.invalid')}>{i}</Alert>)}
      <Panel
        title={t('channels.nav.rules')}
        subtitle={t('channels.rules.subtitle')}
        actions={writable ? <Button icon="add" size="sm" disabled={Boolean(data?.pullRequest)} onClick={() => { setFailure(null); setDraft({ ...EMPTY_RULE, teamsChannel: data?.channels[0]?.id ?? null }); }}>{t('channels.rules.add')}</Button> : undefined}
        flush
      >
        <DataTable
          height={FULL_HEIGHT}
          label={t('channels.nav.rules')}
          rows={data?.rules ?? []}
          rowKey="id"
          loading={routing.isPending}
          toolbar={false}
          columns={[
            { key: 'event', label: t('channels.rules.columns.event'), width: 190, render: (r: Rule) => t(`channels.event.${r.event}`) },
            {
              key: 'active', label: t('channels.rules.columns.state'), width: 150,
              render: (r: Rule) => {
                const s = suspensionOf(r);
                // RG-009-8: a member who may not see the perimeter suspends the channel for this rule (why: in Destinations).
                if (s) return <Badge tone="warn" title={t('channels.rules.suspendedBy', { members: s.members.join(', ') })}>{t('channels.rules.suspended')}</Badge>;
                return <Badge tone={r.active ? 'pos' : 'neutral'}>{r.active ? t('channels.rules.active') : t('channels.rules.disabled')}</Badge>;
              },
            },
            // The environment completes the scope on the same line (« Tous les clients · Production »).
            { key: 'scope', label: t('channels.rules.columns.scope'), width: 200, render: (r: Rule) => `${r.client ? [r.client, r.project].filter(Boolean).join(' › ') : t('channels.rules.allClients')} · ${t(`channels.environment.${r.environment}`)}` },
            { key: 'minSeverity', label: t('channels.rules.columns.severity'), width: 140, render: (r: Rule) => t(`channels.severity.${r.minSeverity}`) },
            {
              key: 'destinations', label: t('channels.rules.columns.destinations'), width: 200, grow: true,
              render: (r: Rule) => {
                const s = suspensionOf(r);
                return (
                  <Inline gap={4} wrap={false}>
                    {destinationTags(r).map(d => <Tag key={d}>{d}</Tag>)}
                    {s && <Text variant="caption" tone="mute" truncate>{t('channels.rules.suspendedBy', { members: s.members.join(', ') })}</Text>}
                  </Inline>
                );
              },
            },
            ...(writable ? [{ key: 'actions', label: t('channels.rules.columns.actions'), width: 120, sortable: false, render: (r: Rule) => <Button variant="ghost" size="sm" disabled={Boolean(data?.pullRequest)} onClick={() => void toggle(r)}>{r.active ? t('channels.rules.disable') : t('channels.rules.enable')}</Button> }] : []),
          ]}
          empty={<EmptyState icon="alt_route" title={t('channels.rules.empty')} />}
        />
      </Panel>

      <Drawer
        open={draft !== null}
        onClose={() => setDraft(null)}
        size="xl"
        title={t('channels.rules.add')}
        subtitle={t('channels.rules.addSubtitle')}
        footer={<Inline gap={8} justify="end"><Button variant="secondary" onClick={() => setDraft(null)}>{t('channels.cancel')}</Button><PendingButton icon="merge" pending={sending} onClick={() => void submit()}>{t('channels.rules.submit')}</PendingButton></Inline>}
      >
        {draft && (
          <Stack gap={16}>
            {failure && failure.body.code === 'VALIDATION_FAILED'
              ? <ErrorSummary title={t('channels.rules.errorsTitle')} errors={((failure.body.details?.fields ?? []) as { field: string; message: string }[]).map(f => ({ message: f.message }))} />
              : failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
            <FormSection title={t('channels.rules.what')} layout="stacked">
              <Select label={t('channels.rules.columns.event')} value={draft.event} onChange={v => setDraft({ ...draft, event: v ?? draft.event })}
                options={(data?.events ?? []).map(e => ({ value: e, label: t(`channels.event.${e}`) }))} />
              <Combobox label={t('channels.rules.columns.scope')} searchable value={draft.client} placeholder={t('channels.rules.allClients')}
                onChange={(v: string | null) => setDraft({ ...draft, client: v, project: null })} onQueryChange={setClientQuery} filter={false}
                options={(clients.data?.items ?? []).map(c => ({ value: c.name, label: c.title }))} error={fieldError('client')} />
              <Grid columns="2" gap={16}>
                <Select label={t('channels.rules.columns.environment')} value={draft.environment} onChange={v => setDraft({ ...draft, environment: v ?? 'all' })}
                  options={(data?.environments ?? []).map(e => ({ value: e, label: t(`channels.environment.${e}`) }))} error={fieldError('environment')} />
                <Select label={t('channels.rules.columns.severity')} value={draft.minSeverity} onChange={v => setDraft({ ...draft, minSeverity: v ?? 'warning' })}
                  options={(data?.severities ?? []).map(e => ({ value: e, label: t(`channels.severity.${e}`) }))} />
              </Grid>
            </FormSection>
            <FormSection title={t('channels.rules.columns.destinations')} layout="stacked">
              <CheckboxGroup label={t('channels.rules.destinationsLabel')} value={draft.destinations} onChange={(v: string[]) => setDraft({ ...draft, destinations: v })}
                error={fieldError('destinations') ?? fieldError('onCallSms')}
                options={[
                  { value: 'teams', label: t('channels.dest.teams') },
                  { value: 'onCallPrivate', label: t('channels.dest.onCallPrivate') },
                  { value: 'teamEmail', label: t('channels.dest.teamEmail') },
                  { value: 'onCallSms', label: t('channels.dest.onCallSms'), description: t('channels.dest.smsProd') },
                ]} />
              {draft.destinations.includes('teams') && (
                <Select label={t('channels.dest.channel')} value={draft.teamsChannel} onChange={v => setDraft({ ...draft, teamsChannel: v })} error={fieldError('teamsChannel')}
                  options={(data?.channels ?? []).map(c => ({ value: c.id, label: c.private ? t('channels.privateChannel', { label: c.label }) : c.label }))} />
              )}
            </FormSection>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

function SimulationSection() {
  const { enums } = useSession();
  const routing = useQuery({ queryKey: ['routing'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/routing')) });
  const [event, setEvent] = useState('availability_incident');
  const [environment, setEnvironment] = useState('prod');
  const [severity, setSeverity] = useState('critical');
  const result = useQuery({
    queryKey: ['simulate', event, environment, severity],
    queryFn: async () => unwrap(await api.maintenance.POST('/notifications/simulate', { body: { event, environment, severity } })),
  });
  return (
    <Stack gap={16}>
      <Panel title={t('channels.nav.simulation')} subtitle={t('channels.simulation.subtitle')}>
        <Stack gap={16}>
          <RadioGroup label={t('channels.rules.columns.event')} value={event} onChange={setEvent}
            options={(routing.data?.events ?? []).slice(0, 4).map(e => ({ value: e, label: t(`channels.event.${e}`) }))} />
          <Grid columns="2" gap={16}>
            <Select label={t('channels.simulation.environment')} value={environment} onChange={v => setEnvironment(v ?? 'prod')}
              options={enums.environments.map(e => ({ value: e.value, label: e.label }))} />
            <Select label={t('channels.simulation.severity')} value={severity} onChange={v => setSeverity(v ?? 'critical')}
              options={(routing.data?.severities ?? []).map(e => ({ value: e, label: t(`channels.severity.${e}`) }))} />
          </Grid>
        </Stack>
      </Panel>
      {result.data && (
        result.data.defaultRuleApplied
          ? <Alert tone="info">{t('channels.simulation.default')}</Alert>
          : (
            <Panel title={t('channels.simulation.wouldSend')} subtitle={t('channels.simulation.matched', { rules: result.data.matchedRules.join(', ') })}>
              <Inline gap={8} wrap>{result.data.destinations.map(d => <Tag key={`${d.kind}-${d.target}`}>{d.label}</Tag>)}</Inline>
            </Panel>
          )
      )}
    </Stack>
  );
}

function OnCallSection() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const oncall = useQuery({ queryKey: ['oncall'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/oncall')) });
  const [editing, setEditing] = useState<{ week: string; level1: string | null; level2: string | null } | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [proposing, setProposing] = useState(false);
  const people = [...new Map((oncall.data?.weeks ?? []).flatMap(w => [w.level1, w.level2]).map(p => [p.email, p])).values()];
  const data = oncall.data;
  const save = async () => {
    if (!editing) return;
    const person = (email: string | null) => people.find(p => p.email === email) ?? { email: email ?? '', name: email ?? '' };
    setProposing(true);
    try {
      unwrap(await api.maintenance.PUT('/notifications/oncall/weeks/{week}', { params: { path: { week: editing.week } }, body: { level1: person(editing.level1), level2: person(editing.level2) } }));
      toast({ tone: 'pos', title: t('channels.oncall.proposed', { week: editing.week }) });
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['oncall'] });
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setProposing(false);
    }
  };
  return (
    <Stack gap={16}>
      {data?.current.level1
        ? <Alert tone="info" title={t('channels.oncall.current', { name: data.current.level1.name, until: shortDate(data.current.until) })}>{t('channels.oncall.level2', { name: data.current.level2?.name ?? '—' })}</Alert>
        : data && <Alert tone="warn" title={t('channels.oncall.nobody', { week: data.current.week })}>{t('channels.oncall.nobodyBody')}</Alert>}
      {(data?.missing ?? []).filter(w => w !== data?.current.week).map(w => <Alert key={w} tone="warn">{t('channels.oncall.missing', { week: w })}</Alert>)}
      {data?.pullRequest && <PullRequestNotice pullRequest={data.pullRequest} />}
      <Panel title={t('channels.nav.oncall')} subtitle={t('channels.oncall.subtitle')} flush>
        <DataTable
          bordered={false}
          height={FULL_HEIGHT}
          label={t('channels.nav.oncall')}
          rows={data?.weeks ?? []}
          rowKey="week"
          loading={oncall.isPending}
          toolbar={false}
          activeRowKey={data?.current.week}
          columns={[
            { key: 'week', label: t('channels.oncall.columns.week'), mono: true, width: 120 },
            { key: 'level1', label: t('channels.oncall.columns.level1'), width: 240, render: (w: Week) => w.level1.name },
            { key: 'level2', label: t('channels.oncall.columns.level2'), width: 240, grow: true, render: (w: Week) => w.level2.name },
            ...(can('oncall.write') ? [{ key: 'actions', label: t('channels.rules.columns.actions'), width: 120, sortable: false, render: (w: Week) => <Button variant="ghost" size="sm" disabled={Boolean(data?.pullRequest)} onClick={() => { setFailure(null); setEditing({ week: w.week, level1: w.level1.email, level2: w.level2.email }); }}>{t('channels.oncall.edit')}</Button> }] : []),
          ]}
          empty={<EmptyState icon="support_agent" title={t('channels.oncall.empty')} />}
        />
      </Panel>
      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="xl"
        title={t('channels.oncall.editTitle', { week: editing?.week ?? '' })}
        footer={<Inline gap={8} justify="end"><Button variant="secondary" onClick={() => setEditing(null)}>{t('channels.cancel')}</Button><PendingButton icon="merge" pending={proposing} onClick={() => void save()}>{t('channels.oncall.submit')}</PendingButton></Inline>}
      >
        {editing && (
          <Stack gap={16}>
            {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>}
            <Select label={t('channels.oncall.columns.level1')} value={editing.level1} onChange={v => setEditing({ ...editing, level1: v })} options={people.map(p => ({ value: p.email, label: p.name }))} />
            <Select label={t('channels.oncall.columns.level2')} value={editing.level2} onChange={v => setEditing({ ...editing, level2: v })} options={people.map(p => ({ value: p.email, label: p.name }))} />
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

/** « Actions depuis Teams » (RG-009-13): adjustable actions by Switch, locked ones shown as forbidden. */
function TeamsPolicySection({ onFooter }: { onFooter: (footer: ReactNode) => void }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const policy = useQuery({ queryKey: ['teams-policy'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/teams-policy')) });
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const writable = can('notifications.write') && !policy.data?.pullRequest;
  const changes = Object.entries(draft).filter(([id, v]) => policy.data?.actions.find(a => a.id === id)?.allowed !== v);
  const save = async () => {
    setSaving(true);
    try {
      const r = unwrap(await api.maintenance.PUT('/notifications/teams-policy', { body: { actions: Object.fromEntries(changes) } }));
      toast({ tone: 'pos', title: t('channels.saved', { ref: changeRef(r.pullRequest) }) });
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['teams-policy'] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setSaving(false);
    }
  };
  useSaveBar(onFooter, changes.length, saving, save, () => setDraft({}));
  type Action = NonNullable<typeof policy.data>['actions'][number];
  return (
    <Stack gap={16}>
      {policy.data?.pullRequest && <PullRequestNotice pullRequest={policy.data.pullRequest} />}
      <Panel title={t('channels.nav.teams')} subtitle={t('channels.teams.subtitle')} flush>
        <DataTable
          bordered={false}
          height={FULL_HEIGHT}
          label={t('channels.nav.teams')}
          rows={policy.data?.actions ?? []}
          rowKey="id"
          loading={policy.isPending}
          toolbar={false}
          columns={[
            { key: 'id', label: t('channels.teams.columns.action'), width: 300, render: (a: Action) => t(`channels.teams.action.${a.id}`) },
            {
              key: 'allowed', label: t('channels.teams.columns.state'), width: 140, sortable: false, editable: true,
              render: (a: Action) => (a.locked
                ? <Inline gap={4} align="center" wrap={false}><Icon name="lock" size={16} /><Badge tone="neutral">{t('channels.teams.forbidden')}</Badge></Inline>
                : <Switch ariaLabel={t(`channels.teams.action.${a.id}`)} checked={draft[a.id] ?? a.allowed} disabled={!writable} onChange={(v: boolean) => setDraft(d => ({ ...d, [a.id]: v }))} />),
            },
            { key: 'effect', label: t('channels.teams.columns.effect'), width: 240, grow: true, mute: true, render: (a: Action) => t(`channels.teams.effect.${a.id}`) },
          ]}
        />
      </Panel>
    </Stack>
  );
}

/** « Options »: repeats grouped in the thread; card updated on acknowledgement and recovery (locked). */
function OptionsSection({ onFooter }: { onFooter: (footer: ReactNode) => void }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ['notification-options'], queryFn: async () => unwrap(await api.maintenance.GET('/notifications/options')) });
  const [threadRepeats, setThreadRepeats] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const writable = can('notifications.write') && !options.data?.pullRequest;
  const changed = threadRepeats !== null && threadRepeats !== options.data?.threadRepeats;
  const save = async () => {
    if (threadRepeats === null) return;
    setSaving(true);
    try {
      const r = unwrap(await api.maintenance.PUT('/notifications/options', { body: { threadRepeats } }));
      toast({ tone: 'pos', title: t('channels.saved', { ref: changeRef(r.pullRequest) }) });
      setThreadRepeats(null);
      void queryClient.invalidateQueries({ queryKey: ['notification-options'] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setSaving(false);
    }
  };
  useSaveBar(onFooter, changed ? 1 : 0, saving, save, () => setThreadRepeats(null));
  return (
    <Stack gap={16}>
      {options.data?.pullRequest && <PullRequestNotice pullRequest={options.data.pullRequest} />}
      <Panel title={t('channels.nav.options')}>
        <FormSection title={t('channels.options.cards')} description={t('channels.options.cardsHint')}>
          <Switch label={t('channels.options.threadRepeats')} checked={threadRepeats ?? options.data?.threadRepeats ?? true} disabled={!writable || !options.data} onChange={(v: boolean) => setThreadRepeats(v)} />
          <Inline gap={8} align="center">
            <Switch label={t('channels.options.updateCard')} checked disabled />
            <Icon name="lock" size={16} />
            <Text variant="body-sm" tone="mute">{t('channels.options.locked')}</Text>
          </Inline>
        </FormSection>
      </Panel>
    </Stack>
  );
}

/** The save bar of a section, in the page footer (« N modifications ouvriront une pull request »). */
function useSaveBar(onFooter: (footer: ReactNode) => void, count: number, saving: boolean, onSave: () => Promise<void>, onCancel: () => void) {
  // The handlers are rebuilt each render: the bar calls the latest ones.
  const handlers = useRef({ onSave, onCancel });
  handlers.current = { onSave, onCancel };
  useEffect(() => {
    onFooter(count ? (
      <SaveBar count={count} message={t('channels.unsaved', { n: count })} saveLabel={t('channels.propose')} cancelLabel={t('channels.cancel')} saving={saving}
        onSave={() => void handlers.current.onSave()} onCancel={() => handlers.current.onCancel()} />
    ) : null);
    return () => onFooter(null);
  }, [count, saving, onFooter]);
}
